import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import './style.scss';
import { Modal } from '../../../Share/components';
import { isIE } from '../../../Share/utils';
import { connect } from 'react-redux';
import { fetchBanners, markBannerAsRead } from '../../actions';
import useWindowSize from '../../../Share/hooks/useWindowSize';

function SingleLineBanner({
  isBannerCrisis = false,
  title = '',
  introduction = '',
  body = '',
  handleBannerClose = () => { },
  onReadMoreClick = () => { },
}) {
  const { isMobileView } = useWindowSize();
  const bannerClass = isBannerCrisis ? 'banner__crisis' : 'banner__info';

  return (
    <div className={`banner__container ${bannerClass}`} key={title}>
      <div className="banner__details">
        <i
          className={`fa ${isBannerCrisis ? 'fa-exclamation-triangle' : 'fa-info-circle'} banner__icon--desktop`}
          aria-hidden="true"
        />
        <div className="banner__information">
          <div className="banner__heading">
            <i
              className={`fa ${isBannerCrisis ? 'fa-exclamation-triangle' : 'fa-info-circle'} banner__icon--mobile`}
              aria-hidden="true"
            />
            <h5>{title}</h5>
          </div>
          <div
            className="banner__description"
            onClick={e => {
              if (e.target.id === 'banner-read-more') {
                onReadMoreClick();
              }
            }}
            onKeyDown={e => {
              if (e.target.id === 'banner-read-more' && e.key === 'Enter') {
                e.preventDefault();
                onReadMoreClick();
              }
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html:
                  introduction
                    .replace(/^<div>|<\/div>$/gi, '')
                    .replace(/^<p>|<\/p>$/gi, '')
                    .trim() +
                  ' <span class="banner__more-info ml-2" tabindex="0" id="banner-read-more">Läs mer</span>',
              }}
            />
          </div>
        </div>
      </div>
      {!isBannerCrisis && (
        <div
          className="banner__close"
          onClick={handleBannerClose}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleBannerClose();
            }
          }}
        >
          {isMobileView ? (
            <i class="fa fa-times" aria-hidden="true" />
          ) : (
            'Stäng meddelande'
          )}
        </div>
      )}
    </div>
  );
}

const propTypes = {
  banners: PropTypes.arrayOf(PropTypes.object),
  fetchBanners: PropTypes.func,
  setBannerAsRead: PropTypes.func,
};

function Banner({
  banners = [],
  fetchBanners = () => { },
  setBannerAsRead = () => { },
}) {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedBannerBody, setSelectedBannerBody] = React.useState('');
  const [selectedBannerTitle, setSelectedBannerTitle] = React.useState('');

  useEffect(() => {
    fetchBanners();
  }, []);

  const lastHandledUnpublishRef = React.useRef(null);

  useEffect(() => {
    const visibleBanners = banners.filter(banner => !banner.IsRead);
    if (visibleBanners.length === 0) return;

    const unpublishTimes = visibleBanners
      .filter(banner => banner.DatetimeUnpublish)
      .map(banner =>
        moment.tz(
          banner.DatetimeUnpublish,
          'M/D/YYYY h:mm:ss A',
          'Europe/Stockholm',
        ).valueOf(),
      );

    if (unpublishTimes.length === 0) return;

    const earliestUnpublish = Math.min(...unpublishTimes);

    if (lastHandledUnpublishRef.current === earliestUnpublish) return;

    const now = Date.now();
    const delay = earliestUnpublish - now;

    if (delay <= 0) {
      lastHandledUnpublishRef.current = earliestUnpublish;
      fetchBanners();
      return;
    }

    const timerId = setTimeout(() => {
      lastHandledUnpublishRef.current = earliestUnpublish;
      fetchBanners();
    }, delay);

    return () => clearTimeout(timerId);
  }, [banners]);

  useEffect(() => {
    const BANNER_POLL_INTERVAL_MS = 5 * 60 * 1000;
    const pollTimerId = setInterval(() => {
      fetchBanners();
    }, BANNER_POLL_INTERVAL_MS);

    return () => clearInterval(pollTimerId);
  }, []);

  const handleBannerRead = id => {
    setBannerAsRead(id);
  };

  const handleReadMoreClick = (body, title) => {
    setSelectedBannerBody(body);
    setSelectedBannerTitle(title);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedBannerBody('');
    setSelectedBannerTitle('');
  };

  return (
    <div className="banner__layout">
      {banners
        .filter(banner => !banner.IsRead)
        .map((banner) => (
          <SingleLineBanner
            key={banner.BannerIdentifier}
            isBannerCrisis={banner.IsBannerCrisis}
            title={banner.Title}
            introduction={banner.Introduction}
            body={banner.Body}
            handleBannerClose={() => handleBannerRead(banner.BannerIdentifier)}
            onReadMoreClick={() => handleReadMoreClick(banner.Body, banner.Title)}
          />
        ))}
      {isModalVisible && (
        <Modal
          isVisible={isModalVisible}
          className="tv-banner-modal__container"
          onCloseClicked={handleCloseModal}
          onOverlayClick={handleCloseModal}
          headerText={selectedBannerTitle}
          ieOptionClassName={isIE ? 'ie-fix-height' : ''}
          headerClassName={`tv-banner-modal-header ${isIE ? 'ie-fix-width' : ''
            }`}
        >
          <div className="tv-banner-modal__content">
            <p
              className="tv-banner-modal__introduction"
              dangerouslySetInnerHTML={{ __html: selectedBannerBody }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

Banner.propTypes = propTypes;

const mapStateToProps = (state, props) => ({
  banners: state.user.banners || [],
});

const mapDispatchToProps = dispatch => ({
  fetchBanners: () => {
    dispatch(fetchBanners());
  },
  setBannerAsRead: bannerId => {
    dispatch(markBannerAsRead(bannerId));
  },
});
export default connect(mapStateToProps, mapDispatchToProps)(Banner);
